<template>
  <PCHeaderBar />

  <div class="pc__listing-detail-page">
    <PCBreadcrumbs :breadcrumbs="breadcrumbList || []" :breadcrumbs:click="handleBreadcrumbsClick" />    

    <PCSku
      v-model:quantity="quantity"
      :detail="listingDetail"
      :skus="skus"
      :promotionInfo="promotionInfo"
      :skuPrice="skuPrice"
      :currentSkuId="currentSkuId"
      :current-selected-sku="currentSelectedSKU"
      :store="storeInfo?.store"
      :delivery-info="deliveryInfo"
      :inventory-status="inventoryStatus"
      @retry:inventory="retryInventory"
      :hasSelectedAddress="!!selectedAddress"
      :address-id="selectedAddress?.id"
      :sku-activate-info="skuActivateInfo"
      :gifts="gifts"
      :review-total="reviewTotal"
      :review-score="reviewScore"
      :trackBaseData="baseTrackData"
      
      @update:sku="handleUpdateSkuClick"
      @activity:over="handleActivityOver"
      @switch:review="handleTabClick('Reviews')"
      @update:quantity="handleQuantityChange"
      @arrival-reminder="handleArrivalReminderSuccess"
    />

    <div class="card-box">
      <StoreCard :store="storeInfo?.store" @to:chat="handleTokfClick"/>
    </div>
    <div class="card-box">
      <div class="store-recommended-div">
        <div class="title">Store Selective</div>
        <div class="store-listing-div">
          <div class="listing-item" v-for="(item, idx) in storeRecommendList" :key="idx">
            <PCProductItemCard :product-data="item" type="store-selective" />
          </div>
        </div>
      </div>
    </div>

    <div class="detail-card" id="pc__product-detail__reviews">
      <div class="tabs">
        <div @click="handleTabClick('Description')" :class="tabName === 'Description' ? 'tab act-tab' : 'tab'">Description</div>
        <div @click="handleTabClick('Reviews')" :class="tabName === 'Reviews' ? 'tab reviews-tab act-tab' : 'tab'">Reviews</div>
        <div @click="handleTabClick('Recommend')" :class="tabName === 'Recommend' ? 'tab reviews-tab act-tab' : 'tab'">Recommend</div>
      </div>

      <div v-show="tabName === 'Description'">
        <div v-if="specification.show" class="specification-card" :style="{ minHeight: specification.list.length ? 'auto' : '330px'}">
          <ClientOnly>
            <Specification :specification="specification.list" />
            <div class="box-description" v-if="listingDetail.box">
              <p class="box-title">What's in the box</p>
              <p class="box-content">{{  listingDetail.box }}</p>
            </div>
            <template #fallback>
              <div class="loading-box">
                <van-loading type="spinner" size="34px" color="#DD3131">Loading...</van-loading>
              </div>
            </template>
          </ClientOnly>
        </div>
        <div
          v-if="listingDescription.show"
          class="product-detail-desc card-box"
          :style="{ minHeight: listingDescription.list.length ? 'auto' : '50vh' }"
        >
          <ClientOnly>
            <KProductDetailRichText :detailItems="listingDescription.list" />
            <template #fallback>
              <div class="loading-box">
                <van-loading type="spinner" size="34px" color="#DD3131">Loading...</van-loading>
              </div>
            </template>
          </ClientOnly>
        </div>
      </div>

      <div class="reviews-card-box" v-show="tabName === 'Reviews'">
        <ClientOnly>
          <KPCReviews :listing-id="listingDetail.listingId" :commentStat="commentStat" />
        </ClientOnly>
      </div>
      <div class="card-box">
        <div class="you-may-like-div">
          <KViewportMount>
            <LazyPCYouMayLike :recommend-id="4" :clsId="clsId" :sku-ids="currentSkuId ? [currentSkuId] : []" :listing-ids="listingDetail.listingId ? [listingDetail.listingId] : []" />
          </KViewportMount>
        </div>
      </div>
    </div>
  </div>

  <PCFooter />
</template>

<script setup lang="ts">
import { usePurchaseRequests } from './usePurchaseRequests'
import { activityKey } from './purchase'
import {
  getListingByIdV2,
  classifyGoodsDetailFailure,
  getGiftsBySkuId,
  getCtgPathByCid,
  getShippingTime,
  getInventoryBySkuId,
  getGoodsGoogleStructuredData,
} from '@/api/models/product'
import { getShippingAddress } from '@/api/models/userInfo'
import { getStoreInfo, getRecommendedProduct } from '@/api/models/store'
import {
  setSEOMeta, updateStatus, disableOutOfStockSpecs, initSkuPrice, trackFn,
  LazyQueue, initSkuId, setStructuredData, tiktokTrack, formatQueryParams,
  toLiveChatSource,
  buildTrafficSourceTracking,
  setReviewListingIdCategoryList, type IListingCategoryItem
} from './mixins'
import { getUrlCache } from '~/api/models/system'
import { buildSourceMetaFromQuery } from '@/utils/traffic-source'
import { buildProductDetailStoreSpecifiedSource, buildShareEntryListingSourceFromQuery, resolveIsChoiceTag } from '@/plugins/utils'

import PCSku from './pc-widgets/Sku.vue'
import { type IBreadcrumbs } from '@/components/PC/Breadcrumbs.vue'
import { type IGifts } from '@/components/KGifts/Card.vue'
import StoreCard from './pc-widgets/StoreCard.vue'
import KPCReviews from './pc-widgets/Reviews.vue'
import Specification from './pc-widgets/Specification.vue'
import { getCommentStatistics } from '~/api/models/reviews'
import { ssrMeasure } from '@/utils/ssr-timing'

definePageMeta({ layout: 'wap', notLoading: true })
const route = useRoute()
const router = useRouter()
const queue = new LazyQueue()
const { id: paramsId } = route.params
const [id] = (paramsId as string).split('-')
const nuxtApp = useNuxtApp()
const { $currencySymbol, $SENSORS_EVENT, $sensors, $getExternalLastTrafficSource, $formatStringToURL } = nuxtApp

const cacheKey = `${id}_${useCookie('kilimall_cache_key').value}`
let query = route.query
if(cacheKey) {
  // type/skuId/from 等关键参数只在 url-cache 中，必须完整等待；仅优化为进程内直读，不做超时丢弃
  const res = await ssrMeasure(
    'listing-pc.url-cache',
    { server: process.server },
    async() => {
      if(import.meta.server) {
        return await useRequestEvent()?.context?.readUrlCacheParams?.(cacheKey) ?? null
      }
      return getUrlCache(cacheKey)
    }
  )
  if(res) { query = res }
}
let trafficSourceMeta = buildSourceMetaFromQuery(query as Record<string, unknown>)
let { type, source, skuId, addressId, from, isAd, isNewTag, isChoiceTag, requestId, algorithmModelName } = query

const shareEntrySource = buildShareEntryListingSourceFromQuery(route.query as Record<string, unknown>)
if (shareEntrySource) {
  source = shareEntrySource
  trafficSourceMeta = undefined
}

// 商品详情
const listingDetail = ref<ProductV3.ListingDetailV3>({
  beenFollowed: false,
  listingId: 0,
  selectedSkuId: 0,
  imageUrl: '',
  imageUrls: [],
  title: '',
  shippingType: 0,
  videoUrl: '',
  dashVideoUrl: '',
  faqUrl: '',
  sizeChartPicture: '',
  box: '',
  prepayment: { state: -1, prepaymentRatio: 1, isSupportMergeCommit: false },
  specs: [],
  props: [],
  details: [],
  categoriesPath: [],
  skus: [],
  storeId: 0,
  storeStatus: 1,
  freeShippingPromotion: { name: '', copyWriting: '' },
  crossStoreReduction: [],
  shippingTime: {
    skuId: 0,
    listingId: 0,
    shippingTimeDescription: '',
    agingStart: 0,
    agingEnd: 0,
    logisticsType: 'fbk'
  },
  vouchersInfo: { gens: [], earnedVouchers: [] },
  selectedSkuGifts: [],
  selectedSkuActivity: null
})

const headers = useRequestHeaders()
const proto = headers['x-forwarded-proto'] || 'https'
const host = `${proto}://${process.server ? headers.host : window.location.host}`
// 商品类型：0 普通商品  1： 秒杀商品
const isFlashSaleSku = from === 'flash-sale'
const detailType: 'sku' | 'listing' = type === 'sku' || from === 'sku' || isFlashSaleSku ?  'sku' : 'listing'

// skus
const skus = ref<ProductV3.Sku[]>([])
const userAddressList = ref<Address.IAddressItem[]>([])
const selectedAddress = computed<Address.IAddressItem | undefined>(() => {
  let defaultAddr = userAddressList.value.find((c: Address.IAddressItem) => c.default)
  
  if(addressId) {
    defaultAddr = userAddressList.value.find((c: Address.IAddressItem) => c.id === Number(addressId))
  }
  
  return defaultAddr
})

// Store 信息
const storeInfo = ref<Store.IResult>()
// 物流时效信息
const deliveryInfo = ref<ProductV3.ShippingTime>()
// Sku 价格缓存
// Sku 价格
// const skuPrice = ref<ProductV2.ISkuPrice>({ salePrice: 0, listPrice: 0, isSale: true, discount: 0, source: null })
const gifts = ref<IGifts[]>([])
// 当前选中的 SKU ID
const currentSkuId = ref<number>(0)
const currentSelectedSKU = computed<ProductV3.Sku | undefined>(() => {
  const sku = skus.value.find((c: ProductV3.Sku) => c.skuId === currentSkuId.value)

  return sku
})

const skuPrice = computed(() => initSkuPrice(currentSelectedSKU.value))
const skuActivateInfo = ref<ProductV3.SelectedSkuActivity>()

const trackError = (e: any) => {
  process.client && $sensors.track(
    $SENSORS_EVENT.goodsDetailsScreen,
    {
      verificationStatus: e.code === '0',
      failureType: e.code,
      failureReason: e.msg,
      listing_id: listingDetail.value?.id || '',
      sku_id: currentSkuId.value || '',
      ...$getExternalLastTrafficSource()
    }
  )
}
const storeRecommendList: any = ref([])
const getStoreRecommendList = async(storeId: number | string) => {
  const { listings } = await getRecommendedProduct(storeId, false).then((r: any) => r.data)
  listings.forEach((item: any) => {
    // 埋点名称
    item.source = buildProductDetailStoreSpecifiedSource()
    storeRecommendList.value.push(item)
  })
}

const updateSkuInventory = () => purchaseRequests.refreshInventory()

const breadcrumbList = ref<IBreadcrumbs[]>([{ name: 'Home', id: -1, path: '/' }])
const initBreadcrumbs = async(): Promise<any> => {
  const res = await getCtgPathByCid(listingDetail.value.categoriesPath.slice(-1)[0]?.id, true)

  const structuredItems: { name: string, position: number, item: string, '@type': 'ListItem' }[] = [];

  (res || []).forEach((c: any, index: number) => {
    const { id, name } =c

    const _source = ((source || `category-allCategory-${$formatStringToURL(name)}`) as string).replaceAll('-', '|')
    const path = `${host}/category/${$formatStringToURL(name)}?backCid=${id}&form=category&source=${_source}`

    breadcrumbList.value.push({ name, id, path })
    structuredItems.push({ name,  position: index + 2, item: path, '@type': 'ListItem' })
  })
  
  breadcrumbList.value.push({ name: listingDetail.value.title, id: 0 })
  
  if(currentSkuId.value) {
    breadcrumbList.value.push({ name: `SKU ID: ${currentSkuId.value}`, id: currentSkuId.value })
  }
  
  return structuredItems
}
/** 图文详情 */
const listingDescription = ref<{ show: boolean, list: any[] }>({ list: [], show: true })
const specification = ref<{ show: boolean, list: { name: string, value: string[] }[]}>({ list: [], show: true })
const initListingDescription = () => {
  const { props: detailProps, details} = listingDetail.value
  specification.value.list = (detailProps || []).map(c => ({ name: c.name, value: c.values.map(c => c.value) }))
  listingDescription.value.list = details || []

  if(detailProps?.length <= 0) {
    specification.value.show = false
  }
  if(details?.length <= 0) {
    listingDescription.value.show = false
  }
}

/** 初始化 大促 / 满减 信息 */
const promotionInfo = reactive<ProductV2.IPromotion>({
  bigPromotion: [],
  freeShippingPromotion: {}
})
const initPromotion = async() => {
  const { crossStoreReduction, freeShippingPromotion } = listingDetail.value
  
  promotionInfo.freeShippingPromotion = freeShippingPromotion
  promotionInfo.bigPromotion = crossStoreReduction
}


interface IGetListingDetailV2 {
  productId?: number | string
  skuId?: number | string
  source?: 0 | 1
  traffic_source_tracking?: string
}
const trafficSourceTracking = buildTrafficSourceTracking({
  source: source as string,
  trafficSourceMeta,
  isAd,
  isNewTag: !!isNewTag,
  isChoiceTag: !!isChoiceTag,
  requestId: requestId as string,
  algorithmModelName: algorithmModelName as string
})
const paramsV2:IGetListingDetailV2  = {
  source: isFlashSaleSku ? 1 : 0,
  skuId: skuId ? Number(skuId) : undefined,
  traffic_source_tracking: trafficSourceTracking
}
if(detailType === 'sku') {
  paramsV2.skuId = Number(id)
} else {
  paramsV2.productId = Number(id)
}

let listingResultV2: any = null
let error: any = null
let retried = false
try {
  listingResultV2 = await ssrMeasure(
    'listing-pc.goods-detail',
    { server: process.server, retried: false },
    () => getListingByIdV2(paramsV2)
  )
} catch(e) {
  // 如果是listing详情报错了，进行 skuid 详情重试
  if(!paramsV2.skuId && paramsV2.productId) {
    retried = true
    paramsV2.skuId = paramsV2.productId
    paramsV2.productId = ''

    listingResultV2 = await ssrMeasure(
      'listing-pc.goods-detail',
      { server: process.server, retried: true },
      () => getListingByIdV2(paramsV2)
    ).then((r: any) => {
        type = 'sku'

        return r
      }).catch((e: any) => {
        error = e
      })
  } else {
    error = e
  }
}
if(!listingResultV2) {
  const failure = classifyGoodsDetailFailure(error)
  const throwData = {
    statusCode: failure.statusCode,
    message: '',
    fatal: failure.expected ? process.client : true
  }
  
  // 违规下架
  if(failure.code === 5004) {
    throwData.message = 'The product does not exist.'
  }
  if(failure.expected) {
    if(Math.random() < 0.01) {
      console.info('[goods-detail] expected outcome:', {
        code: failure.code,
        status: failure.statusCode,
        retried,
        requestType: type
      })
    }
  } else {
    console.error('getListingByIdV2 ERROR: ', {
      code: failure.code,
      retried,
      error
    })
  }
  
  throw createError(throwData)
}

listingDetail.value = listingResultV2

const clsId = computed(() => {
  if (listingDetail.value && listingDetail.value.categoriesPath && listingDetail.value.categoriesPath.length) {
    const id = listingDetail.value.categoriesPath.slice(-1)[0]?.id
    return id
  }
  return 0
})


const {
  selectedSkuId, skus: skusV2, selectedSkuGifts,
  selectedSkuActivity, shippingTime
} = listingDetail.value

skus.value = skusV2 || []
deliveryInfo.value = shippingTime
skuActivateInfo.value = selectedSkuActivity!

if(detailType === 'sku') {
  currentSkuId.value = Number(id)
} 
if(skuId) {
  if(skusV2.findIndex(c => c.skuId === Number(skuId)) >= 0) {
    currentSkuId.value = Number(skuId)
  }
}

if(currentSkuId.value <= 0) {
  currentSkuId.value = initSkuId(selectedSkuId, skusV2) || 0
}

gifts.value = selectedSkuGifts || []

initPromotion()
initListingDescription()


try{
  const breadcrumbsTask = ssrMeasure(
    'listing-pc.ctg-path',
    { server: process.server },
    initBreadcrumbs
  )
  const googleStructuredDataTask = process.server
    ? ssrMeasure(
        'listing-pc.google-sd',
        { server: true },
        () => getGoodsGoogleStructuredData({
          listing_id: listingDetail.value.listingId,
          sku_id: currentSkuId.value || undefined
        }).catch((e: any) => {
          console.error('[getGoodsGoogleStructuredData] pc:', e)
          return null
        })
      )
    : Promise.resolve(null)
  const [structuredData, googleStructuredData] = await Promise.all([
    breadcrumbsTask,
    googleStructuredDataTask
  ])

  if(process.server) {
    nuxtApp.runWithContext(() => setStructuredData(googleStructuredData, structuredData))
  }
} catch(e) {
  console.error(`[structuredData]sku#: ${currentSkuId.value}: `, JSON.stringify(e))
}

const quantity = ref(1)
const purchaseRequests = usePurchaseRequests({
  listing: listingDetail, sku: currentSelectedSKU, skuId: currentSkuId,
  activity: skuActivateInfo, quantity, address: selectedAddress, delivery: deliveryInfo,
  fetchInventory: (id) => nuxtApp.runWithContext(() => getInventoryBySkuId(id, {
    ...(isFlashSaleSku ? { activity_type: 1 } : {}),
    ...(route.query.activateId ? { activate_id: route.query.activateId as string } : {})
  })),
  fetchShipping: (params) => nuxtApp.runWithContext(() => getShippingTime(params))
})
const { inventoryStatus } = purchaseRequests
const retryInventory = async() => {
  giftRequest++
  gifts.value = []
  const token = purchaseRequests.capture()
  const ready = await updateSkuInventory()
  if (!ready || !purchaseRequests.isCurrent(token)) return
  await Promise.all([initDeliveryWarehouse(), initGift()])
  if (purchaseRequests.isCurrent(token) && currentSelectedSKU.value) {
    updateStatus({ skuKey: currentSelectedSKU.value.key, skus: skus.value, specs: listingDetail.value.specs }, skuActivateInfo.value)
  }
}

const handleQuantityChange = (val: number) => { quantity.value = val }
watch(quantity, () => { void initDeliveryWarehouse() }, { flush: 'sync' })

const handleArrivalReminderSuccess = (reminderState: boolean) => {
  const sku = skus.value.find((c: ProductV3.Sku) => c.skuId === currentSkuId.value)
  if(sku) {
    sku.isArrivalReminder = reminderState
  }
}
// 初始化 SKU 图片(含Color的规格才会显示图片)
const colorSpec = listingDetail.value.specs.find(v=> v.name.toUpperCase().indexOf('COLOR') >= 0)
if(colorSpec) {
  const { values, pid } = colorSpec
  
  values.forEach(val => {
    const _sku = skus.value.find((c: ProductV3.Sku) => c.key.indexOf(`${pid}:${val.pvid}`) >= 0)
    
    if(_sku && _sku.images?.length > 0) {
      val.image = _sku.images[0] || ''
    }
  })
}

const initDeliveryWarehouse = () => purchaseRequests.refreshDelivery()
watch(selectedAddress, () => { void initDeliveryWarehouse() })

const initUserAddressOr = async() => {
  if(useUserInfo().value?.accessToken) {
    const res = await getShippingAddress(false).catch((e: any) => { console.error('用户地址初始化异常 >>>>> ', e) })

    userAddressList.value = res || []
  }

  // await initDeliveryWarehouse()
}


let giftRequest = 0
const initGift = async() => {
  const token = purchaseRequests.capture(), request = ++giftRequest
  gifts.value = []
  if (!currentSelectedSKU.value?.haveGift) return
  try {
    const { giftActivities = [] } = await nuxtApp.runWithContext(() => getGiftsBySkuId(token.skuId))
    if (purchaseRequests.isCurrent(token) && request === giftRequest) gifts.value = giftActivities
  } catch { /* Gifts are optional; stale results must never replace the current selection. */ }
}
/** 评论统计 */
const reviewTotal = ref<number>(0)
const reviewScore = ref<string>('5.0')
const commentStat = ref<any>()

const initRatingInfo = async() => {
  const commentRes = await getCommentStatistics({
    listingId: listingDetail.value.listingId,
    storeId: storeInfo.value!.store.id
  })

  const commentTask = () => {
    reviewScore.value = commentRes.listingScore
    reviewTotal.value = commentRes.commentTotalNum

    storeInfo.value!.store.starRating = commentRes.storeScore
  }
    
  commentStat.value = commentRes
  queue.addTask(commentTask)
}

const skuTrackFn = () => {
  const token = purchaseRequests.capture()
  const data = { ...baseTrackData.value }
  if (trackSkuIdMap[token.skuId]) return
  queue.addTask(() => {
    if (!purchaseRequests.isCurrent(token) || trackSkuIdMap[token.skuId]) return
    trackFn(data, $SENSORS_EVENT.viewProductDetail)
    trackSkuIdMap[token.skuId] = 1
  })
}

const clientInit = () => {
  // 提前将无库存的规格标记
  disableOutOfStockSpecs(listingDetail.value.specs, skus.value)
  // 更新与当前选中规格相关规格的状态
  if(currentSelectedSKU.value) {
    updateStatus(
      { skuKey: currentSelectedSKU.value.key, skus: skus.value, specs: listingDetail.value.specs },
      skuActivateInfo.value
    )
  }

  const fns: (() => Promise<void>)[] = [initRatingInfo, initUserAddressOr]

  Promise.all([
    getStoreInfo(listingDetail.value.storeId).then((r: any) => r.data), // 初始化店铺信息
    getStoreRecommendList(listingDetail.value.storeId)
  ]).then(([storeRes]) => {
    storeInfo.value = storeRes

    Promise.all(fns.map(fn => fn())) // 初始化地址及时效信息

    queue.addTask(skuTrackFn)

    queue.addTask(() => {
      tiktokTrack(
        'ViewContent',
        1,
        currentSelectedSKU.value,
        listingDetail.value
      )
    })
  })
}

setSEOMeta(listingDetail.value, skuPrice.value)

if(process.client && listingDetail.value.listingId) {    
  clientInit()
  queue.addTask(() => { trackError({ code: '0', msg: 'success' }) })
}
/** --------------- EVENT START----------------- */
const tabName = ref<'Description' | 'Reviews' | 'Recommend'>('Description')
const handleTabClick = (e: 'Description' | 'Reviews' | 'Recommend') => {
  tabName.value = e
}

const trackSkuIdMap: { [key:string]: number } = {}
const baseTrackData = computed(() => {
  const { categoriesPath } = listingDetail.value
  const categoryId = categoriesPath[categoriesPath.length - 1]?.id

  return {
    source: source as string,
    trafficSourceMeta,
    skuId: currentSkuId.value,
    listingId: listingDetail.value.listingId,
    categoryId: categoryId,
    store: storeInfo.value?.store,
    skuPrice: skuPrice.value,
    isAd: isAd === 'ad' || isAd,
    isNewTag: !!isNewTag,
    isChoiceTag: !!isChoiceTag || resolveIsChoiceTag(listingDetail.value.highlights),
    requestId: (requestId as string) || '',
    algorithmModelName: (algorithmModelName as string) || '',
    productInventoryType: deliveryInfo.value?.logisticsType!,
    productTitle: listingDetail.value.title
  }
})

const handleBreadcrumbsClick = (item: IBreadcrumbs) => {
  if(item.path === '/') {
    window.location.href = '/'
  }

  if(item.id <= 0 || !item.path) {
    return
  }
  
  // 跳转至搜索页
  window.open(item.path)
}

const handleUpdateSkuClick = async(skuKey: string, sku?: ProductV3.Sku) => {
  if(sku?.skuId === currentSkuId.value) {
    return
  }
  
  currentSkuId.value = sku?.skuId || 0
  gifts.value = []
  breadcrumbList.value.pop()
  
  if(currentSkuId.value <= 0) { return }
  
  breadcrumbList.value.push({ name: `SKU ID: ${currentSkuId.value}`, id: currentSkuId.value })
  const token = purchaseRequests.capture()
  const ready = await updateSkuInventory()
  if (!ready || !purchaseRequests.isCurrent(token)) return
  await Promise.all([initDeliveryWarehouse(), initGift()])
  if (!purchaseRequests.isCurrent(token)) return
  updateStatus({ skuKey, skus: skus.value, specs: listingDetail.value.specs }, skuActivateInfo.value)
  skuTrackFn()
}

/** 活动结束 刷新SKU */
const handleActivityOver = (activity: ProductV3.SelectedSkuActivity) => {
  if (!purchaseRequests.expireActivity(activity)) return
  showToast('The current activity has ended.')
  void retryInventory()
}

const handleTokfClick = () => {
  if(!useUserInfo().value?.userId) {
    router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
    return
  }

  let { listingId, imageUrl, title } = listingDetail.value

  if(currentSelectedSKU.value) {
    imageUrl = currentSelectedSKU.value.images[0] || imageUrl
  }

  const { id: storeId, name, storeIcon } = storeInfo.value!.store
  let _price = skuPrice.value.salePrice || ''
   
  if(skuActivateInfo.value && [1, 4, 6].includes(skuActivateInfo.value?.activityType)){
    const { startTime, endTime } = skuActivateInfo.value
    const st = new Date(startTime).getTime()
    const et = new Date(endTime).getTime()
    const curT = new Date().getTime()
    
    if(curT >= st && curT <= et) {
      _price = skuActivateInfo.value?.activityPrice
    }
  }

  const productMsg: Msg.IMsgProduct = {
    goodsId: listingId,
    goodsName: title,
    goodsPic: imageUrl || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    goodsUnit: !currentSelectedSKU.value?.inventory ? 'Out of stock' : $currencySymbol(),
    goodsPrice: _price || '', // skuPrice.value.salePrice || '',
    storeId: storeId,
    storeName: name,
    storeIcon,
    source: toLiveChatSource(source as string, trafficSourceMeta)
  }

  usePCOpenRoomInfo().value = { source: source as string, msgType: 3, product: productMsg, chatFirstPage: 'productDetail' }
  usePCLiveChatPopupShow().value = true
}

/** --------------- EVENT END----------------- */
// 商品的品类数组
const categoryList: Ref<Array<number>> = ref([])
onMounted(() => {
  if (listingDetail.value && listingDetail.value.categoriesPath && Array.isArray(listingDetail.value.categoriesPath)) {
    // 提取 id 并去重
    const ids = listingDetail.value.categoriesPath.map((item: any) => item.id)
    categoryList.value = [...new Set(ids)]
    const listingCategoryItem: IListingCategoryItem = {
      listingId: listingDetail.value.listingId,
      categoryList: categoryList.value,
      timestamp: Date.now()
    }
    setReviewListingIdCategoryList(listingCategoryItem)
  }
})
</script>

<style lang="scss" scoped>
.pc__listing-detail-page{
  width: var(--viewport-width);
  margin: 0 auto;
}
.card-box{
  margin: 30px 0;
  background-color: #fff;
}
.you-may-like-div {
  padding: 40px;
}
.reviews-card-box{
  background-color: #fff;
  padding: 0 20px;
}
.store-recommended-div {
  width: 100%;
  padding: 20px 40px 20px 20px;
  box-sizing: border-box;
  .title {
    font-size: 38px;
    height: 70px;
    font-weight: 600;
    margin-left: 20px;
    margin-bottom: 10px;
    border-bottom: 1px solid #efefef;
  }
  .store-listing-div {
    width: 100%;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    .listing-item {
      width: 18.18%;
      display: block;
      min-width: 18.18%;
      padding-right: 10px;
    }
  }
}
.loading-box{
  height: 260px;
  background-color: rgba($color: #fff, $alpha: 0.4);
  width: 100%;
  @include center();
}
.detail-card{
  .tabs{
    background-color: #fff;
    display: flex;
    border-bottom: 1px solid #DDDFE4;
    .tab{
      cursor: pointer;
      text-align: center;
      padding: 25px 50px;
      font-size: 38px;
      color: #333;
      transition: color .15s;
    }
    .act-tab{
      color: #fff;
      background-color: var(--pc-default-color);
    }
  }
  .specification-card{
    background-color: #fff;
    .box-description{
      padding: 30px;
      .box-title{
        font-size: 38px;
        font-weight: 600;
        border-bottom: 1px solid #efefef;
        padding-bottom: 20px;
        margin-bottom: 20px;
      }
    }
  }
  .product-detail-desc{
    min-height: 100px;
    background-color: #fff;
  }
  :deep(.detail-text){
    font-size: 32px;
  }
}

</style>
